import { z } from 'zod';
import { LABEL_DIRECTIONS, LABEL_MODES, SETUP_TAGS } from './labels.js';
/**
 * What is actually in the label dataset (B5).
 *
 * Every later block reads these labels, so this is the layer that answers whether they are
 * worth reading. Three things are measured and they answer different questions: the
 * STATISTICS say what the dataset contains, QUALITY CONTROL says what is wrong with it, and
 * AGREEMENT says how much of a hindsight label was hindsight.
 *
 * ## Why every figure is a pair
 *
 * `Figure` carries its own `n`, and nothing in this file exposes a bare number that a screen
 * could render without one. That is a type-level answer to a real failure: a 68% win rate over
 * 19 trades is not a win rate, it is noise with a decimal point, and it will be quoted as
 * though it meant something the moment it appears on a dashboard without its denominator
 * beside it. Making the count inseparable from the value means no component can forget it and
 * no later refactor can drop it.
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const instant = z.string().datetime({ offset: true });
/**
 * A number and the number of trades behind it. Never one without the other.
 *
 * `n` is the count of labels the value was computed FROM, which is not always the dataset
 * total: a win rate over labels with outcomes is over a different population from a count by
 * tag, and reporting both against the same denominator is how a dashboard starts lying
 * quietly.
 */
export const figureSchema = z.object({
    value: z.number().nullable(),
    n: z.number().int(),
});
export const figure = (value, n) => ({
    value: n === 0 ? null : value,
    n,
});
/**
 * A distribution, reported by quantile rather than by mean and standard deviation.
 *
 * Trade returns are not normal — they are skewed, fat-tailed, and frequently bimodal once
 * stops are involved — so a mean and a σ describe a shape the data does not have. The median
 * and the quartiles describe the shape it does have, and the min and max show the tail that a
 * summary statistic is designed to hide. The mean is kept anyway, because the difference
 * between the mean and the median IS the skew and is worth being able to see.
 */
export const distributionSchema = z.object({
    n: z.number().int(),
    min: z.number().nullable(),
    p25: z.number().nullable(),
    median: z.number().nullable(),
    p75: z.number().nullable(),
    max: z.number().nullable(),
    mean: z.number().nullable(),
});
export const bucketSchema = z.object({ key: z.string(), n: z.number().int() });
export const labelCountsSchema = z.object({
    bySymbol: z.array(bucketSchema),
    byTag: z.array(bucketSchema),
    byDirection: z.array(bucketSchema),
    byMode: z.array(bucketSchema),
    byMonth: z.array(bucketSchema),
    byConfidence: z.array(bucketSchema),
});
/** One point on the MFE/MAE scatter, with the identity needed to click through to its label. */
export const scatterPointSchema = z.object({
    labelId: z.string().uuid(),
    symbol: z.string(),
    sessionDate: isoDate,
    direction: z.enum(LABEL_DIRECTIONS),
    mode: z.enum(LABEL_MODES),
    /** Percent. Favourable is positive in the trade's own direction; adverse is negative. */
    mfePct: z.number(),
    maePct: z.number(),
    rMultiple: z.number().nullable(),
});
export const labelStatisticsSchema = z.object({
    generatedAt: instant,
    /** Current, non-retracted labels. The population every count below is drawn from. */
    total: z.number().int(),
    superseded: z.number().int(),
    retracted: z.number().int(),
    counts: labelCountsSchema,
    /** Labels with a `label_outcome` row. C3 fills that table; until it runs this is zero. */
    withOutcome: z.number().int(),
    winRate: figureSchema,
    returnPct: distributionSchema,
    rMultiple: distributionSchema,
    barsHeld: distributionSchema,
    mfePct: distributionSchema,
    maePct: distributionSchema,
    /** Entries by quarter-hour of the New York session, over the decision window. */
    entryClock: z.array(bucketSchema),
    scatter: z.array(scatterPointSchema),
});
// ---------------------------------------------------------------------------------------
// Quality control
// ---------------------------------------------------------------------------------------
/**
 * Defects are things B2 refuses on write, so any that exist are historical or a bug in
 * something that bypassed it. Outliers are not wrong — they are worth a second look, and
 * automatically rejecting them would quietly delete the most interesting trades in the set.
 */
export const QC_SEVERITIES = ['defect', 'outlier'];
export const QC_KINDS = [
    'duplicate',
    'overlap',
    'below_gate_no_override',
    'price_outside_bar',
    'outside_decision_window',
    'reserved_labelled_in_hindsight',
    'extreme_r',
    'one_bar_hold',
    'edge_of_window',
    'sparse_features',
];
export const qcFindingSchema = z.object({
    kind: z.enum(QC_KINDS),
    severity: z.enum(QC_SEVERITIES),
    labelId: z.string().uuid(),
    symbol: z.string(),
    sessionDate: isoDate,
    entryTs: instant,
    /** One sentence naming the actual value, so the finding is actionable without a second read. */
    detail: z.string(),
});
export const labelQcSchema = z.object({
    generatedAt: instant,
    checked: z.number().int(),
    defects: z.array(qcFindingSchema),
    outliers: z.array(qcFindingSchema),
    /** Every kind that was run, so "nothing found" is distinguishable from "not checked". */
    ranChecks: z.array(z.enum(QC_KINDS)),
});
// ---------------------------------------------------------------------------------------
// Agreement — the measurement this project is built on
// ---------------------------------------------------------------------------------------
/**
 * A hindsight label and a replay label judged to be the same trade.
 *
 * The matching rule is the part worth arguing about, so it is written down rather than
 * implied: two labels are the same trade when they are on the same symbol and session, in the
 * same direction, and their held intervals overlap. Candidates are paired greedily by closest
 * entry, nearest first.
 *
 * Overlap rather than "entry within N bars" because the question being asked is "did the
 * replay pass find this trade", and a replay entry three bars later on the same move is the
 * same trade found later — which is exactly the lateness the timing figures are there to
 * measure. A fixed tolerance would silently reclassify the most interesting cases, the ones
 * where hindsight entered at a low that replay could not have known was a low, as two separate
 * trades: one missed and one invented. That would double-count a single disagreement and
 * flatter the timing numbers by dropping the worst pairs out of them.
 */
export const agreementPairSchema = z.object({
    hindsightId: z.string().uuid(),
    replayId: z.string().uuid(),
    symbol: z.string(),
    sessionDate: isoDate,
    direction: z.enum(LABEL_DIRECTIONS),
    /** Replay minus hindsight, in minutes. Positive means replay was later, which is the norm. */
    entryDeltaMinutes: z.number(),
    exitDeltaMinutes: z.number(),
    hindsightReturnPct: z.number(),
    replayReturnPct: z.number(),
    /** Replay minus hindsight. Null when either side has no R. */
    rDelta: z.number().nullable(),
});
export const unmatchedLabelSchema = z.object({
    labelId: z.string().uuid(),
    symbol: z.string(),
    sessionDate: isoDate,
    direction: z.enum(LABEL_DIRECTIONS),
    entryTs: instant,
    returnPct: z.number(),
});
export const labelAgreementSchema = z.object({
    generatedAt: instant,
    /** Sessions labelled in BOTH modes. Everything below is restricted to these. */
    sessions: z.number().int(),
    hindsightLabels: z.number().int(),
    replayLabels: z.number().int(),
    /** Found by replay and also marked in hindsight. */
    matched: z.array(agreementPairSchema),
    /** Marked in hindsight, not found in replay. The hindsight illusion, itemised. */
    missedByReplay: z.array(unmatchedLabelSchema),
    /** Taken in replay, not considered worth marking in hindsight. */
    inventedByReplay: z.array(unmatchedLabelSchema),
    /**
     * The headline: replay's realised return as a fraction of hindsight's, over matched pairs.
     * The ceiling on what any model could have captured in real time.
     */
    captureRate: figureSchema,
    entryDeltaMinutes: distributionSchema,
    exitDeltaMinutes: distributionSchema,
    rDelta: distributionSchema,
});
// ---------------------------------------------------------------------------------------
// Drift
// ---------------------------------------------------------------------------------------
/**
 * A supersession, as the raw before-and-after.
 *
 * Deliberately NOT a rendered sentence: B3's `describeChange` already turns a pair into
 * English, it lives in the web package beside the edit flow that uses it, and a second
 * implementation on the server would drift from it within a month. The server ships the pair
 * and the page describes it, so there is one description of a change in this system.
 */
export const driftPairSchema = z.object({
    supersededId: z.string().uuid(),
    replacementId: z.string().uuid(),
    reason: z.string().nullable(),
    symbol: z.string(),
    sessionDate: isoDate,
    /** Days between the original label and its correction — how long the second look took. */
    daysApart: z.number(),
    before: z.object({
        direction: z.enum(LABEL_DIRECTIONS),
        entryTs: instant,
        entryPrice: z.string(),
        exitTs: instant,
        exitPrice: z.string(),
        confidence: z.number().int(),
        setupTag: z.array(z.enum(SETUP_TAGS)),
    }),
    after: z.object({
        direction: z.enum(LABEL_DIRECTIONS),
        entryTs: instant,
        entryPrice: z.string(),
        exitTs: instant,
        exitPrice: z.string(),
        confidence: z.number().int(),
        setupTag: z.array(z.enum(SETUP_TAGS)),
    }),
});
export const labelDriftSchema = z.object({
    generatedAt: instant,
    total: z.number().int(),
    supersededCount: z.number().int(),
    retractedCount: z.number().int(),
    /** Superseded labels as a share of every label ever written. */
    supersessionRate: figureSchema,
    pairs: z.array(driftPairSchema),
    /**
     * Sessions labelled twice in the SAME mode, which is self-agreement rather than mode
     * comparison. Poor self-agreement is a finding about the task definition — the setup
     * vocabulary or the confidence scale needs sharpening — and not about the labeller.
     */
    repeatedSessions: z.array(z.object({
        symbol: z.string(),
        sessionDate: isoDate,
        mode: z.enum(LABEL_MODES),
        passes: z.number().int(),
        labels: z.number().int(),
    })),
});
export const labelAnalysisQuerySchema = z
    .object({
    symbol: z
        .string()
        .regex(/^[A-Z][A-Z0-9.]{0,9}$/)
        .optional(),
    mode: z.enum(LABEL_MODES).optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
})
    .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: 'from must not be after to',
    path: ['to'],
});
//# sourceMappingURL=labelAnalysis.js.map